import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, DataTable, EmptyState, FilterBar, FilterChips, IconButton, Pagination, Pill, type Column } from "@atlas/ui/web";
import { atlas } from "../../../../lib/sdk";
import type { BancoSubOrganizacao } from "@atlas/sdk";
import { POR_PAGINA_PADRAO, recorteDoServidor } from "../../../../lib/recorte";
import { useDebouncedValue } from "../../../../lib/use-debounce";
import { useFiltros } from "../../../../lib/use-filtros";

/**
 * Sub organizacoes do banco — as opcoes do campo "Sub organizacao" no
 * cadastro do operador. Nunca se apaga: desativa-se, e a desativada deixa de
 * ser oferecida a operador novo sem sair de quem ja estava nela.
 */
export function BancoSubOrganizacoesLista() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [situacao, setSituacao] = useState<"" | "ativo" | "inativo">("");
  const [search, setSearch] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  const buscaDebounced = useDebouncedValue(search);

  const data = useQuery({
    queryKey: ["banco", "sub-organizacoes", situacao, buscaDebounced, pagina, porPagina],
    queryFn: () =>
      atlas.banco.listSubOrganizacoes({
        situacao: situacao || undefined,
        q: buscaDebounced || undefined,
        limit: porPagina,
        offset: (pagina - 1) * porPagina,
      }),
    placeholderData: (anterior) => anterior,
  });

  const filtros = useFiltros(
    [
      { nome: "busca", ligado: search !== "", limpar: () => setSearch("") },
      { nome: "situacao", ligado: situacao !== "", limpar: () => setSituacao("") },
    ],
    setPagina,
  );
  const { filtrar } = filtros;

  const daPagina = recorteDoServidor(data.data?.subOrganizacoes ?? [], data.data?.total ?? 0, pagina, porPagina);

  const invalidar = (id: string) => {
    qc.invalidateQueries({ queryKey: ["banco", "sub-organizacoes"] });
    qc.invalidateQueries({ queryKey: ["banco", "sub-organizacao", id] });
  };
  const remove = useMutation({
    mutationFn: (id: string) => atlas.banco.removerSubOrganizacao(id),
    onSuccess: (_d, id) => invalidar(id),
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => atlas.banco.reativarSubOrganizacao(id),
    onSuccess: (_d, id) => invalidar(id),
  });

  const columns: Column<BancoSubOrganizacao>[] = [
    {
      key: "ativo",
      header: "Situação",
      render: (s) => <Pill variant={s.ativo ? "averbado" : "expirado"}>{s.ativo ? "Ativo" : "Inativo"}</Pill>,
    },
    { key: "nome", header: "Nome" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Cadastros
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Sub organizações</h1>
        </div>
        <Button onClick={() => nav("novo")}>+ Inserir</Button>
      </header>

      <FilterBar
        searchValue={search}
        onSearchChange={filtrar(setSearch)}
        {...filtros.propsFilterBar}
      >
        <FilterChips
          label="Situação"
          value={situacao}
          onChange={(v) => filtrar(setSituacao)(v as "" | "ativo" | "inativo")}
          options={[
            { value: "", label: "Todas" },
            { value: "ativo", label: "Ativas" },
            { value: "inativo", label: "Inativas" },
          ]}
        />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(s) => s.id}
        loading={data.isLoading}
        emptyState={
          data.isError
            ? "Não foi possível carregar as sub organizações."
            : <EmptyState
                motivo={filtros.temFiltro ? "filtro-vazio" : "sem-dados"}
                filtroVazio="Nenhuma sub organização bate com os filtros atuais."
                semDados="Nenhuma sub organização cadastrada. Clique em '+ Inserir' para criar a primeira."
                onLimpar={filtros.limparFiltros}
              />
        }
        actions={(s) => (
          <>
            <IconButton title="Editar" onClick={() => nav(s.id)}>✎</IconButton>
            {s.ativo ? (
              <IconButton
                title="Desativar"
                danger
                onClick={() => {
                  if (confirm(`Desativar a sub organização "${s.nome}"?\n\nEla deixa de aparecer para novos operadores, mas nada é apagado — você pode reativar depois.`)) remove.mutate(s.id);
                }}
              >
                ⏸
              </IconButton>
            ) : (
              <IconButton title="Reativar" onClick={() => reactivate.mutate(s.id)}>▶</IconButton>
            )}
          </>
        )}
      />
      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="sub organizações"
      />
    </div>
  );
}
